import { createTheme } from "@mui/material";

// -- Violet / Purple ---------------------------------------------------------
const violet = {
    50:  '#f5f3ff',
    100: '#ede9fe',
    200: '#ddd6fe',
    300: '#c4b5fd',
    400: '#a78bfa',
    500: '#8b5cf6',
    600: '#7c3aed',
    700: '#6d28d9',
    800: '#5b21b6',
    900: '#4c1d95',
};

// -- Sky / Ocean Blue --------------------------------------------------------
const sky = {
    400: '#38bdf8',
    500: '#0ea5e9',
    600: '#0284c7',
};

// -- Teal --------------------------------------------------------------------
const teal = {
    400: '#2dd4bf',
    500: '#14b8a6',
    600: '#0d9488',
};

// -- Theme factory -----------------------------------------------------------
export const createAppTheme = (mode) => {
    const isDark = mode === 'dark';

    return createTheme({
        palette: {
            mode,
            primary: {
                main:         isDark ? violet[400] : violet[600],
                light:        isDark ? violet[300] : violet[500],
                dark:         isDark ? violet[500] : violet[700],
                contrastText: '#ffffff',
            },
            secondary: {
                main:         isDark ? sky[400] : sky[500],
                contrastText: '#ffffff',
            },
            background: {
                default: isDark ? '#1c1c1e' : '#f7f6f3',
                paper:   isDark ? '#242424'  : '#ffffff',
            },
            text: {
                primary:   isDark ? '#f0f0f0' : '#1a1a1a',
                secondary: isDark ? '#a0a0a0' : '#6b7280',
                disabled:  isDark ? '#555555' : '#9ca3af',
            },
            divider:  isDark ? '#333333' : '#e8e8e6',
            error:    { main: '#ef4444' },
            warning:  { main: '#f59e0b' },
            success:  { main: teal[500] },
            info:     { main: sky[500] },
        },

        typography: {
            fontFamily: '"DM Sans", sans-serif',
            h1: { fontWeight: 800, fontSize: '64px' },
            h2: { fontWeight: 700, fontSize: '48px' },
            h3: { fontWeight: 700, fontSize: '36px' },
            h4: { fontWeight: 600, fontSize: '28px' },
            h5: { fontWeight: 600, fontSize: '22px' },
            h6: { fontWeight: 600, fontSize: '18px' },
            subtitle1: { fontWeight: 500, fontSize: '16px' },
            subtitle2: { fontWeight: 500, fontSize: '15px' },
            body1:     { fontWeight: 400, fontSize: '14px' },
            body2:     { fontWeight: 400, fontSize: '13px' },
            button:    { fontWeight: 600, fontSize: '13px' },
            caption:   { fontWeight: 400, fontSize: '12px' },
        },

        components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: '12px',
                    minWidth: '100px',
                    minHeight: '38px',
                    transition: 'all 0.22s ease',
                    position: 'relative',
                    overflow: 'hidden',
                },
                contained: {
                    backgroundColor: isDark ? violet[500] : violet[600],
                    color: '#fff',
                    boxShadow: isDark
                        ? '0 2px 10px rgba(139,92,246,0.35)'
                        : '0 2px 10px rgba(124,58,237,0.25)',
                    '&:hover': {
                        backgroundColor: isDark ? violet[400] : violet[500],
                        boxShadow: isDark
                            ? '0 6px 24px rgba(139,92,246,0.5)'
                            : '0 6px 24px rgba(124,58,237,0.4)',
                        transform: 'translateY(-2px)',
                    },
                    '&:active': {
                        transform: 'translateY(0)',
                        boxShadow: 'none',
                    },
                },
                outlined: {
                    borderColor: isDark ? 'rgba(139,92,246,0.5)' : 'rgba(124,58,237,0.4)',
                    color: isDark ? violet[400] : violet[600],
                    '&:hover': {
                        backgroundColor: isDark ? 'rgba(139,92,246,0.08)' : 'rgba(124,58,237,0.06)',
                        borderColor: isDark ? violet[400] : violet[600],
                        transform: 'translateY(-1px)',
                        boxShadow: isDark
                            ? '0 4px 14px rgba(139,92,246,0.22)'
                            : '0 4px 14px rgba(124,58,237,0.15)',
                    },
                },
                text: {
                    color: isDark ? violet[400] : violet[600],
                    '&:hover': {
                        backgroundColor: isDark ? 'rgba(139,92,246,0.08)' : 'rgba(124,58,237,0.06)',
                    },
                },
            },
        },
        MuiIconButton: {
            styleOverrides: {
                root: {
                    borderRadius: '10px',
                    '&:hover': {
                        backgroundColor: isDark ? 'rgba(139,92,246,0.1)' : 'rgba(124,58,237,0.06)',
                    },
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    backgroundImage: 'none',
                    background: isDark ? '#242424' : '#ffffff',
                    border: isDark ? '1px solid #333333' : '1px solid #e8e8e6',
                    borderRadius: '20px',
                    boxShadow: isDark
                        ? '0 4px 20px rgba(0,0,0,0.35)'
                        : '0 4px 20px rgba(0,0,0,0.07)',
                },
            },
        },
        MuiSkeleton: {
            styleOverrides: {
                root: {
                    backgroundColor: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)',
                    borderRadius: '8px',
                    '&::after': {
                        background: isDark
                            ? 'linear-gradient(90deg,transparent,rgba(255,255,255,0.05),transparent)'
                            : 'linear-gradient(90deg,transparent,rgba(255,255,255,0.7),transparent)',
                    },
                },
            },
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    borderRadius: '8px',
                    fontSize: '12px',
                    backgroundColor: isDark ? '#2d2d2d' : '#1a1a1a',
                    color: isDark ? '#e0e0e0' : '#f0f0f0',
                },
                arrow: { color: isDark ? '#2d2d2d' : '#1a1a1a' },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: { borderRadius: '12px', borderColor: 'grey' },
            }
        },
        MuiFormLabel:      { styleOverrides: { root: { color: 'grey' } } },
        MuiFormHelperText: { styleOverrides: { root: { fontSize: '12.5px', color: 'grey' } } },
        MuiPopover:        { styleOverrides: { paper: { borderRadius: '12px' } } },
        MuiBackdrop: {
            styleOverrides: {
                root: {
                    backgroundColor: isDark ? 'rgba(0,0,0,0.65)' : 'rgba(0,0,0,0.4)',
                    backdropFilter: 'blur(4px)',
                }
            }
        },
        MuiLink: {
            styleOverrides: {
                root: {
                    fontWeight: 500,
                    fontSize: '14px',
                    cursor: 'pointer',
                    '&[disabled]': {
                        color: 'grey',
                        textDecoration: 'none',
                        pointerEvents: 'none',
                    },
                },
            }
        },
        MuiTableCell: {
            styleOverrides: {
                head: {
                    color: 'grey', backgroundColor: '#fafafa',
                    borderColor: '#fafafa', padding: '8px 16px',
                },
                body: { borderColor: '#f1f1f1', padding: '8px 16px' }
            }
        },
        MuiTablePagination: {
            styleOverrides: {
                root: {},
                selectLabel: { color: 'grey' },
            },
        },
        MuiTabs: {
            styleOverrides: {
                root: {
                    minHeight: '40px', height: '40px',
                    maxHeight: '40px', marginBottom: '10px'
                },
                indicator: {
                    bottom: '0px', height: '0px',
                    backgroundColor: isDark ? violet[400] : violet[600],
                },
            }
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    fontWeight: '600',
                    minHeight: '40px', height: '40px', maxHeight: '40px',
                    borderRadius: '12px', marginRight: '10px',
                    '&:hover': {
                        backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                    },
                    '&.Mui-selected': {
                        color: isDark ? violet[400] : violet[600],
                        backgroundColor: isDark ? 'rgba(139,92,246,0.1)' : 'rgba(124,58,237,0.08)',
                    }
                },
            }
        },
        MuiDivider: {
            styleOverrides: {
                root: { borderColor: isDark ? '#333333' : '#e8e8e6' },
            },
        },
        MuiSwitch: {
            styleOverrides: {
                root: { padding: 6 },
                thumb: {
                    backgroundColor: isDark ? violet[400] : violet[500],
                    boxShadow: 'none', width: 18, height: 18, margin: 1,
                },
                track: {
                    border: '1.5px solid',
                    borderColor: isDark ? violet[500] : violet[600],
                    backgroundColor: isDark ? '#161616' : 'white',
                    borderRadius: 26 / 2,
                    '&::before, &::after': {
                        content: '""', position: 'absolute',
                        top: '50%', transform: 'translateY(-50%)',
                        width: 18, height: 18,
                    },
                },
            }
        },
        MuiChip: {
            styleOverrides: {
                labelSmall: { fontSize: '12px' },
                filled: {
                    fontWeight: 500,
                    '&.MuiChip-colorError':   { backgroundColor: '#fadfd8', color: '#b24020' },
                    '&.MuiChip-colorSuccess': { backgroundColor: '#d7ebce', color: '#44712e' },
                    '&.MuiChip-colorInfo':    { backgroundColor: '#dce6f1', color: '#004182' },
                    '&.MuiChip-colorWarning': { backgroundColor: '#fce2ba', color: '#915907' },
                },
                outlined: {
                    fontWeight: 500,
                    '&.MuiChip-colorError':   { backgroundColor: '#fadfd8', color: '#b24020' },
                    '&.MuiChip-colorSuccess': { backgroundColor: '#d7ebce', color: '#44712e' },
                    '&.MuiChip-colorInfo':    { backgroundColor: '#dce6f1', color: '#004182' },
                    '&.MuiChip-colorWarning': { backgroundColor: '#fce2ba', color: '#915907' },
                },
            }
        },
        MuiMenu:         { styleOverrides: { paper: { minWidth: '200px', maxWidth: '500px' } } },
        MuiToggleButton: {
            styleOverrides: {
                root: {
                    borderRadius: '12px', minHeight: '53.13px',
                    padding: '11px 20px', fontWeight: '600', fontSize: '14px',
                },
            }
        },
        MuiAccordion: {
            styleOverrides: {
                root: { borderRadius: '12px', boxShadow: 'none', backgroundColor: '#f1f1f1' }
            }
        },
        MuiAccordionSummary: {
            styleOverrides: { root: { borderRadius: '12px' } },
        },
        MuiAvatar: {
            styleOverrides: {
                root: {
                    border: `2px solid ${isDark ? violet[700] : violet[200]}`,
                },
            },
        },
        },
    });
};

// backward-compat named export
export const theme = createAppTheme('light');
